import React, { useMemo, useState } from 'react';
import { Clock3Icon, MapPinIcon, MessageSquareTextIcon, UsersIcon } from 'lucide-react';
import type { Pharmacy } from '../lib/platformData';
import { pharmacies } from '../lib/platformData';
type SortOption = 'all' | 'wait' | 'care' | 'stock';
type FilterOption = {
  id: SortOption;
  label: string;
};
type TrafficPresentation = {
  label: 'Light' | 'Moderate' | 'Heavy';
  className: string;
};
const filters: FilterOption[] = [{
  id: 'all',
  label: 'All Pharmacies'
}, {
  id: 'wait',
  label: '⏱️ Shortest Wait Times'
}, {
  id: 'care',
  label: '🤝 Highest Staff Care'
}, {
  id: 'stock',
  label: '💊 Verified Stock Confidence'
}];
const approximateLocalDistances: Record<string, string> = {
  northridge: '~0.8 mi',
  lakeview: '~1.4 mi',
  wellcare: '~2.7 mi',
  parkway: '~1.1 mi'
};
export function CommunityPharmacyExplorer() {
  const [activeFilter, setActiveFilter] = useState<SortOption>('all');
  const [reviewDetailsId, setReviewDetailsId] = useState<string | null>(null);
  const results = useMemo(() => getSortedPharmacies(activeFilter).slice(0, 3), [activeFilter]);
  return <section className="w-full bg-white py-14 sm:py-16" aria-labelledby="community-explorer-title" data-id="element-49" data-mp="3jvflarsenw7x">
      <div className="mx-auto w-full px-6 md:px-12 lg:px-16" data-id="element-50" data-mp="3ewm2qky9hf0w">
        <div data-id="element-51" data-mp="3eyfegenqepfr">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#008F82]" data-id="element-52" data-mp="3eyowng2jbsaw">
            Find your pharmacies
          </p>
          <h2 id="community-explorer-title" className="mt-1 text-[26px] font-black tracking-tight text-[#1C1C1C]" data-id="element-53" data-mp="2m8gmaae8qc6v">
            pharmacy near you
          </h2>
          <p className="mt-2 max-w-[660px] text-sm leading-6 text-[#686868]" data-id="element-54" data-mp="2w5l9mdjtodp9">
            Sort by what matters most — wait times, staff care, or stock
            confidence. Local distances are approximate, not live location data.
          </p>
        </div>

        <div className="mt-6 flex items-center gap-3 overflow-x-auto pb-4" role="toolbar" aria-label="Sort community pharmacies" data-id="element-55" data-mp="bhyj8g4micz6">
          {filters.map(filter => {
          const selected = activeFilter === filter.id;
          return <button key={filter.id} type="button" onClick={() => setActiveFilter(filter.id)} aria-pressed={selected} className={`shrink-0 rounded-full border px-4 py-2.5 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#008F82] focus-visible:ring-offset-2 ${selected ? 'border-[#E28743] bg-[#E28743] text-white' : 'border-[#D8D8D4] bg-white text-[#1C1C1C] hover:border-[#E28743] hover:text-[#A65425]'}`} data-id="element-56" data-mp="sex1o116nxfm">
                {filter.label}
              </button>;
        })}
        </div>

        <div className="border-t border-gray-100" aria-live="polite" data-id="element-57" data-mp="3i620e2sbvjp4">
          {results.map(pharmacy => {
          const reviewDetailsOpen = reviewDetailsId === pharmacy.id;
          return <article key={pharmacy.id} className="border-b border-gray-100 py-6" data-id="element-58" data-mp="1v9vk7ubh5dwb">
                <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between xl:gap-8" data-id="element-59" data-mp="1q97sf6ff8t11">
                  <div className="min-w-0 flex-1" data-id="element-60" data-mp="1lawhsthayehr">
                    <h3 className="text-base font-black text-[#1C1C1C]" data-id="element-61" data-mp="1gc35h4dr4tj6">
                      {pharmacy.name}
                    </h3>
                    <p className="mt-1 text-xs font-medium text-[#8A8A8A]" data-id="element-62" data-mp="1bktfu3s7dq7a">
                      {pharmacy.category} · {pharmacy.neighborhood}
                    </p>
                    {!pharmacy.claimed && <p className="mt-4 max-w-xl border-l-2 border-[#E7B158] pl-3 text-xs leading-5 text-[#526158]" data-id="element-63" data-mp="12mhp81rc6seu">
                        🏢 Are you the owner? Claim this profile free to respond
                        to reviews.
                      </p>}
                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-64" data-mp="2sqbaft2pvxrg">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#008F82]" data-id="element-65" data-mp="2xn1pqe6p6pbt">
                        <Clock3Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-66" data-mp="2iy5hoh4n5cb7" />
                        Open today
                      </span>
                      <button type="button" onClick={() => setReviewDetailsId(reviewDetailsOpen ? null : pharmacy.id)} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#A65425] underline underline-offset-4 hover:text-[#7E361F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#008F82]" aria-expanded={reviewDetailsOpen} aria-controls={`review-details-${pharmacy.id}`} data-id="element-67" data-mp="29x7e1zy476t5">
                        <MessageSquareTextIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-68" data-mp="1irs0yc9kkn77" />
                        View review
                      </button>
                    </div>
                    {reviewDetailsOpen && <p id={`review-details-${pharmacy.id}`} className="mt-3 max-w-xl bg-[#F4F8F5] px-3 py-2 text-xs leading-5 text-[#355343]" data-id="element-69" data-mp="26ftfq2xcczv2">
                        {pharmacy.reviewCount} structured community experiences
                        are available for this pharmacy.
                      </p>}
                  </div>
                  <ScoreMatrix pharmacy={pharmacy} data-id="element-70" data-mp="m19my0kygsrh" />
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}
function ScoreMatrix({
  pharmacy
}: {
  pharmacy: Pharmacy;
}) {
  const traffic = getTrafficPresentation(pharmacy.footTraffic.status);
  const metrics = [{
    label: 'Stock',
    value: pharmacy.scores.stock.toFixed(1),
    className: 'bg-[#EEF5F0] text-[#008F82]'
  }, {
    label: 'Wait',
    value: pharmacy.scores.wait.toFixed(1),
    className: 'bg-[#FFF1E8] text-[#A65425]'
  }, {
    label: 'Care',
    value: pharmacy.scores.care.toFixed(1),
    className: 'bg-[#F1F4F2] text-[#008F82]'
  }, {
    label: 'Foot traffic',
    value: traffic.label,
    className: traffic.className,
    Icon: UsersIcon
  }, {
    label: 'Distance',
    value: approximateLocalDistances[pharmacy.id] ?? 'Nearby',
    className: 'bg-[#F7F7F7] text-[#555]',
    Icon: MapPinIcon
  }];
  return <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-5 xl:w-[500px] xl:shrink-0" data-id="element-71" data-mp="3iv2wwzf26wmn">
      {metrics.map(({
      Icon,
      ...metric
    }) => <div key={metric.label} className={`min-w-0 rounded-sm px-2.5 py-2.5 text-center ${metric.className}`} data-id="element-72" data-mp="1rez80q8hyj7">
          <p className="flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wide" data-id="element-73" data-mp="3svlt9621tk61">
            {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden="true" data-id="element-74" data-mp="3o06hqkrik0cs" />}
            <span className="truncate" data-id="element-75" data-mp="3jxtm4p6hditq">{metric.label}</span>
          </p>
          <p className="mt-1 truncate text-sm font-black tabular-nums" data-id="element-76" data-mp="khwry5b2gu4k">
            {metric.value}
          </p>
        </div>)}
    </div>;
}
function getTrafficPresentation(status: Pharmacy['footTraffic']['status']): TrafficPresentation {
  if (status === 'Busy') {
    return {
      label: 'Heavy',
      className: 'bg-[#FBEDEA] text-[#A63A2B]'
    };
  }
  if (status === 'Moderate') {
    return {
      label: 'Moderate',
      className: 'bg-[#FFF4DD] text-[#95611A]'
    };
  }
  return {
    label: 'Light',
    className: 'bg-[#EEF5F0] text-[#008F82]'
  };
}
function getSortedPharmacies(filter: SortOption) {
  const sortedPharmacies = [...pharmacies];
  if (filter === 'wait') return sortedPharmacies.sort((a, b) => b.scores.wait - a.scores.wait);
  if (filter === 'care') return sortedPharmacies.sort((a, b) => b.scores.care - a.scores.care);
  if (filter === 'stock') return sortedPharmacies.sort((a, b) => b.scores.stock - a.scores.stock);
  return sortedPharmacies.sort((a, b) => b.reviewCount - a.reviewCount);
}